/**
 * `GET /api/site-info`, as the footer actually needs it.
 *
 * ── WHY A GAME-OWNED COPY OF A CORE TYPE ────────────────────────────────────
 * The backend's `SiteInfoController` returns `paymentMethods` — it was added to
 * that response specifically so a footer could show the enabled brands. But
 * `frontend/src/core/preview/preview.ts` declares a `SiteInfo` interface with
 * no `paymentMethods` field, and that file is template-owned: editing it is a
 * merge conflict we would carry forever for one property.
 *
 * So this module declares the full shape the server sends, and reads the
 * payload core has ALREADY fetched and cached (`getSiteInfo()`), rather than
 * asking for it a second time. `main.ts` awaits core's `fetchSiteInfo()` before
 * the first render, so by the time a footer mounts the value is normally there;
 * when it is not — a test, a direct import, a screen mounted before boot
 * finished — we fetch it ourselves.
 *
 * ── PREVIEW MODE IS A REAL STATE, NOT AN EDGE CASE ──────────────────────────
 * With `PREVIEW_BUILD=true` the server deliberately returns a blank company,
 * no social links and no payment methods, and calls the game "Game Preview".
 * A footer that renders empty headings, an empty logo strip and "© 2026 ."
 * would be a broken-looking page, so every block below is conditional on real
 * content existing and `isPreview` drives an explicit notice instead. The rule
 * is: nothing is invented to fill a gap, and a gap is never presented as data.
 */
import { api } from '../../core/network/api';
import { getSiteInfo as coreCachedSiteInfo } from '../../core/preview/preview';

/** One enabled payment brand. `icon` is the SVG basename in `public/payment-methods/`. */
export interface PaymentMethodRef {
  readonly slug: string;
  readonly name: string;
  readonly icon: string;
}

/** One configured social profile. `platform` is a settings key such as `discord`. */
export interface SocialLinkRef {
  readonly platform: string;
  readonly url: string;
  /** Stem of a file in `/social/`. Blank means "render the label only". */
  readonly icon: string;
}

export interface SiteCompany {
  readonly name: string;
  readonly email: string;
  readonly address: string;
  readonly registration: string;
  readonly jurisdiction: string;
  readonly country: string;
  readonly domain: string;
  readonly gameName: string;
}

export interface SiteFeatures {
  readonly kyc: boolean;
  readonly withdrawals: boolean;
  readonly invoices: boolean;
}

/** The whole `/site-info` payload, `paymentMethods` included. */
export interface GameSiteInfo {
  readonly preview: boolean;
  readonly company: SiteCompany;
  readonly features: SiteFeatures;
  readonly socialLinks: readonly SocialLinkRef[];
  readonly paymentMethods: readonly PaymentMethodRef[];
}

/** Where the shipped brand SVGs live. Mirrors core's `ICON_URL_PREFIX`. */
export const PAYMENT_ICON_PREFIX = '/payment-methods/';

/**
 * Social marks ship as static SVGs under `/social/`, addressed BY FILENAME —
 * `SocialLink.icon` is the stem. That indirection is what lets an operator
 * replace a placeholder mark with a brand's official asset by dropping a file
 * in, with no migration and no code change (see `public/social/NOTE.txt`).
 */
export const SOCIAL_ICON_PREFIX = '/social/';

/**
 * Every mark that ships, in picker order — the brands first, the two generic
 * glyphs last. The admin console draws its picker from this list and the
 * footer renders whatever a link names; a link whose `icon` is not here still
 * renders, it just has no file behind it, which is why the footer keeps the
 * text label as the accessible name rather than relying on the image.
 *
 * The brand marks are simple-icons' official ones (CC0-1.0); `website` and
 * `email` are drawn in-house. See `public/social/NOTE.txt`.
 */
export const SOCIAL_ICONS: readonly string[] = Object.freeze([
  'x',
  'youtube',
  'twitch',
  'discord',
  'telegram',
  'instagram',
  'facebook',
  'tiktok',
  'linkedin',
  'whatsapp',
  'reddit',
  'snapchat',
  'steam',
  'bluesky',
  'rss',
  'website',
  'email',
]);

/** Absolute path to a shipped social mark. */
export function socialIconUrl(icon: string): string {
  return `${SOCIAL_ICON_PREFIX}${icon}.svg`;
}

/**
 * What the footer renders when the endpoint is unreachable: a site with no
 * company details rather than a site claiming to be nobody. `gameName` keeps
 * the neutral placeholder core uses, so a failed request and a preview build
 * degrade the same way instead of producing two different kinds of wrong.
 */
export const EMPTY_SITE_INFO: GameSiteInfo = Object.freeze({
  preview: false,
  company: Object.freeze({
    name: '',
    email: '',
    address: '',
    registration: '',
    jurisdiction: '',
    country: '',
    domain: '',
    gameName: 'Game',
  }),
  features: Object.freeze({ kyc: false, withdrawals: false, invoices: false }),
  socialLinks: Object.freeze([]),
  paymentMethods: Object.freeze([]),
});

function str(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function bool(value: unknown): boolean {
  return value === true;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Coerce whatever the server (or core's cache, or a stale deploy) hands over
 * into `GameSiteInfo`.
 *
 * Deliberately total: an older backend that predates `paymentMethods`, a
 * proxy that returned HTML, `null` — all of them produce the empty site rather
 * than an exception thrown from inside a footer, because a footer must never
 * be the reason a page fails to render.
 */
export function normaliseSiteInfo(raw: unknown): GameSiteInfo {
  if (!isRecord(raw)) return EMPTY_SITE_INFO;
  const company = isRecord(raw.company) ? raw.company : {};
  const features = isRecord(raw.features) ? raw.features : {};

  const socialLinks: SocialLinkRef[] = [];
  if (Array.isArray(raw.socialLinks)) {
    for (const entry of raw.socialLinks) {
      if (!isRecord(entry)) continue;
      const url = str(entry.url);
      const platform = str(entry.platform);
      // A social row with no URL is a mis-configured setting, not a link.
      if (url === '' || platform === '') continue;
      // An unknown stem is kept rather than dropped: the footer treats the
      // mark as decorative, so a link with a missing file still works.
      socialLinks.push({ platform, url, icon: str(entry.icon) });
    }
  }

  const paymentMethods: PaymentMethodRef[] = [];
  if (Array.isArray(raw.paymentMethods)) {
    for (const entry of raw.paymentMethods) {
      if (!isRecord(entry)) continue;
      const slug = str(entry.slug);
      if (slug === '') continue;
      const icon = str(entry.icon);
      paymentMethods.push({
        slug,
        name: str(entry.name) || slug,
        // Fall back to the slug: core's own comment says the two match, and a
        // brand with a blank icon should still be nameable in the alt text.
        icon: icon === '' ? slug : icon,
      });
    }
  }

  return {
    preview: bool(raw.preview),
    company: {
      name: str(company.name),
      email: str(company.email),
      address: str(company.address),
      registration: str(company.registration),
      jurisdiction: str(company.jurisdiction),
      country: str(company.country),
      domain: str(company.domain),
      gameName: str(company.gameName) || EMPTY_SITE_INFO.company.gameName,
    },
    features: {
      kyc: bool(features.kyc),
      withdrawals: bool(features.withdrawals),
      invoices: bool(features.invoices),
    },
    socialLinks,
    paymentMethods,
  };
}

let cached: GameSiteInfo | null = null;
let inFlight: Promise<GameSiteInfo> | null = null;

/**
 * The site info, from core's cache when it has one, otherwise over the wire.
 *
 * Concurrent callers share one request — the footer, the landing header and
 * anything else mounted in the same tick must not each open a socket for a
 * response that never changes within a page load.
 */
export async function loadSiteInfo(): Promise<GameSiteInfo> {
  if (cached !== null) return cached;

  const fromCore = coreCachedSiteInfo();
  if (fromCore !== null) {
    cached = normaliseSiteInfo(fromCore);
    return cached;
  }

  if (inFlight !== null) return inFlight;
  inFlight = api
    .get<unknown>('/site-info')
    .then((raw) => normaliseSiteInfo(raw))
    .catch(() => EMPTY_SITE_INFO)
    .then((info) => {
      cached = info;
      inFlight = null;
      return info;
    });
  return inFlight;
}

/** The cached value, or `null` when nothing has resolved yet. Never fetches. */
export function peekSiteInfo(): GameSiteInfo | null {
  if (cached !== null) return cached;
  const fromCore = coreCachedSiteInfo();
  if (fromCore === null) return null;
  cached = normaliseSiteInfo(fromCore);
  return cached;
}

/** Absolute path to a brand's shipped SVG. */
export function paymentIconUrl(method: PaymentMethodRef): string {
  return `${PAYMENT_ICON_PREFIX}${method.icon}.svg`;
}

/**
 * The name to print as the brand. In a preview build the server has already
 * replaced it with "Game Preview", so there is nothing to special-case here —
 * which is the point of reading branding from the runtime rather than a build
 * constant.
 */
export function brandName(info: GameSiteInfo): string {
  return info.company.name || info.company.gameName;
}

/** Test seam: drop the module-level cache. */
export function resetSiteInfoCacheForTests(): void {
  cached = null;
  inFlight = null;
}
